import {
  Button,
  Card,
  CardProps,
  Flex,
  HStack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiAddLine, RiCheckboxCircleFill, RiLock2Line } from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import React from "react";
import { useWatch } from "react-hook-form";
import { Link } from "react-router";

import { AndOrList } from "@/common/components/AndOrList";
import CardContainer from "@/common/components/CardContainer";
import FormSwitchButtonGroup from "@/common/components/Form/FormSwitchButtonGroup";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import MotionBox from "@/common/components/MotionBox";
import {
  RuleAdvancedDisplay,
  RuleAdvancedForm,
} from "@/common/components/Rule/RuleAdvancedForm";
import { RulesEstimationSingle } from "@/common/components/Rule/RulesEstimationSingle";
import {
  RuleSimpleDisplay,
  RuleSimpleForm,
} from "@/common/components/Rule/RuleSimpleForm";
import { EnvironmentProvider } from "@/common/contexts/EnvironmentContext";
import { EnvironmentDisplayName } from "@/environment/components/EnvironmentDisplayName";
import { EnvironmentHeaderDTO } from "@reflag/shared/environmentAPI";
import {
  CustomRule,
  FeatureTargeting,
  FeatureTargetingVersion,
} from "@reflag/shared/featureTargetingAPI";
import {
  alwaysTrueFilter,
  COMPANY_ID_CONTEXT_FIELD,
  MAX_ROLLOUT_THRESHOLD,
} from "@reflag/shared/filter";

const sharedCardProps: CardProps = {
  p: 4,
  w: "full",
};

type RulesFormProps = {
  baseName: string;
  environment: EnvironmentHeaderDTO;
  fields: (CustomRule & { id: string })[];
  version?: number;
  flagKey?: string;
  append: (value: CustomRule) => void;
  remove: (index: number) => void;
};

export function RulesFormLegacy({
  baseName,
  environment,
  fields,
  version,
  flagKey = "",
  append,
  remove,
}: RulesFormProps) {
  const targeting: FeatureTargeting = useWatch({ name: baseName });
  const { targetingMode } = targeting;

  return (
    <EnvironmentProvider envId={environment.id}>
      <CardContainer gap={1}>
        <Flex direction="row" justify="space-between" pb={1} px={2}>
          <Flex align="center" direction="row" gap={2}>
            <EnvironmentDisplayName environment={environment} fontSize="md" />
            <RulesEstimationSingle flagKey={flagKey} targeting={targeting} />
          </Flex>
          <Flex align="center" direction="row" gap={2}>
            {version && (
              <Text
                as={Link}
                color="dimmed"
                fontSize="sm"
                textDecoration="underline"
                to={`versions?environment=${environment.id}`}
              >
                v{version}
              </Text>
            )}
            <FormSwitchButtonGroup
              buttons={[
                { label: "No one", id: "none" },
                { label: "Some", id: "some" },
                { label: "Everyone", id: "everyone" },
              ]}
              name={`${baseName}.targetingMode`}
              rules={{
                deps: [baseName],
              }}
            />
          </Flex>
        </Flex>
        <AnimatePresence initial={false}>
          {targetingMode === "some" && (
            <MotionBox
              animate={{ opacity: 1, height: "auto" }}
              display="flex"
              exit={{ opacity: 0, height: 0 }}
              flexDirection="column"
              gap={1}
              initial={{ opacity: 0, height: 0 }}
              transition={{ duration: "0.15", ease: "easeInOut" }}
            >
              <AndOrList
                conjunction="or"
                conjunctionProps={{ ml: 4, zIndex: 1 }}
                direction="vertical"
                gap={0}
              >
                <Card {...sharedCardProps}>
                  <RuleSimpleForm baseName={baseName} />
                </Card>
                {fields.map(({ id }, index) => (
                  <Card key={id} {...sharedCardProps}>
                    <RuleAdvancedForm
                      baseName={`${baseName}.customRules.${index}`}
                      remove={() => remove(index)}
                    />
                  </Card>
                ))}
              </AndOrList>
              <Button
                alignSelf="flex-start"
                leftIcon={<RiAddLine size={16} />}
                size="sm"
                variant="ghostDimmed"
                onClick={() =>
                  append({
                    partialRolloutThreshold: MAX_ROLLOUT_THRESHOLD,
                    partialRolloutContextAttribute: COMPANY_ID_CONTEXT_FIELD,
                    filter: alwaysTrueFilter,
                  })
                }
              >
                Add rule
              </Button>
            </MotionBox>
          )}
        </AnimatePresence>
      </CardContainer>
    </EnvironmentProvider>
  );
}

type Props = {
  targeting: FeatureTargetingVersion;
  showVersion?: boolean;
  showEnvironment?: boolean;
  showLockIcon?: boolean;
  highlightCurrentVersion?: boolean;
  flagKey?: string;
};

export const RulesDisplayLegacy = ({
  targeting,
  showVersion = false,
  showEnvironment = false,
  showLockIcon = false,
  highlightCurrentVersion = false,
  flagKey = "",
}: Props) => {
  const {
    environment,
    currentVersion,
    targetingMode,
    segmentIds,
    companyIds,
    userIds,
    customRules,
    version,
  } = targeting;

  const hasSimpleTargeting =
    !!segmentIds.length || !!companyIds.length || !!userIds.length;
  const hasAdvancedTargeting = !!customRules.length;

  const normalBackgroundColor = useColorModeValue("gray.25", "gray.900");
  const normalBorderColor = useColorModeValue("transparent", "gray.800");
  const currentVersionBackgroundColor = useColorModeValue(
    "brand.50",
    normalBackgroundColor,
  );
  const currentVersionBorderColor = useColorModeValue(
    normalBorderColor,
    "brand.800",
  );

  const everyoneCheckmarkColor = useColorModeValue("gray.950", "white");

  return (
    <CardContainer
      backgroundColor={
        highlightCurrentVersion && currentVersion
          ? currentVersionBackgroundColor
          : normalBackgroundColor
      }
      borderColor={
        highlightCurrentVersion && currentVersion
          ? currentVersionBorderColor
          : normalBorderColor
      }
    >
      <Flex direction="row" justify="space-between" pb={1} px={2}>
        <Flex align="center" direction="row" gap={2}>
          {showEnvironment && (
            <EnvironmentDisplayName environment={environment} />
          )}
          <RulesEstimationSingle flagKey={flagKey} targeting={targeting} />
        </Flex>
        <Flex align="center" direction="row" gap={2}>
          {showVersion && version && (
            <Text
              as={Link}
              color="dimmed"
              fontSize="sm"
              textDecoration="underline"
              to={`versions?environment=${environment.id}`}
            >
              v{version}
            </Text>
          )}
          {showLockIcon && (
            <InfoIconTooltip
              icon={<RiLock2Line size={16} />}
              text={`You don't have permission to modify ${environment.name} environment targeting rules.`}
            />
          )}
        </Flex>
      </Flex>
      {targetingMode === "everyone" ? (
        <Card {...sharedCardProps}>
          <HStack spacing={1.5}>
            <RiCheckboxCircleFill color={everyoneCheckmarkColor} size={16} />
            <Text as="span">Everyone has access</Text>
          </HStack>
        </Card>
      ) : targetingMode === "some" &&
        (hasSimpleTargeting || hasAdvancedTargeting) ? (
        <AndOrList
          conjunction="or"
          conjunctionProps={{ ml: 4, zIndex: 1 }}
          direction="vertical"
          gap={0}
        >
          {hasSimpleTargeting && (
            <Card {...sharedCardProps}>
              <RuleSimpleDisplay targeting={targeting} />
            </Card>
          )}
          {customRules.map((targetingRule, i) => (
            <Card key={i} {...sharedCardProps}>
              <RuleAdvancedDisplay targetingRule={targetingRule} />
            </Card>
          ))}
        </AndOrList>
      ) : (
        <Card {...sharedCardProps}>
          <HStack color="dimmed" spacing={1.5}>
            <Text as="span">
              {targetingMode === "none"
                ? "No one has access"
                : "No targeting rules configured"}
            </Text>
          </HStack>
        </Card>
      )}
    </CardContainer>
  );
};
